import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { localizeItem } from '../../hooks/useLanguage';
  import { useTheme } from '../../context/ThemeContext';
import '../Cart.css';
import './CartSheet.css';
import { formatRestaurantPrice } from '../../utils/formatPrice';

const defaultT = {
  orderCreated: 'Заказ принят!', orderNum: 'Заказ №', table: 'Стол',
  toPay: 'К оплате', payPayme: 'Оплатить через Payme', close: 'Закрыть',
  order: 'Оформить заказ', callWaiter: 'Вызвать официанта', askBill: 'Попросить счёт',
  waiterComing: 'Официант уже идёт!', billComing: 'Счёт уже несут!',
  total: 'Итого', comment: 'Комментарий к заказу', commentHint: 'Пожелания, аллергии, без лука...',
};

export function CartSheet({
  items,
  restaurantId,
  tableNumber = 1,
  t = defaultT,
  lang = 'ru',
  currency = 'UZS',
  onClose,
  onClear,
  onAdd,
  onRemove,
}) {
  const theme = useTheme();
  const { colors, isDark } = theme;

  const [isLoading, setIsLoading]   = useState(false);
  const [orderData, setOrderData]   = useState(null);
  const [error, setError]           = useState(null);
  const [callSent, setCallSent]     = useState(null);
  const [comment, setComment]       = useState('');
  const [frozenMsg, setFrozenMsg]   = useState(null);

  const totalAmount = items.reduce((s, i) => s + i.price * i.quantity, 0);

  const handleCreateOrder = async () => {
    if (isLoading || items.length === 0) return;
    setIsLoading(true);
    setError(null);
    setFrozenMsg(null);
    try {
      const { data } = await axios.post('/api/orders/create', {
        restaurantId: restaurantId || 'a0eebc99-9c0b-4ef8-bb6d-6bb9bd380a11',
        tableNumber:  tableNumber || 1,
        items: items.map((i) => ({ menuItemId: i.id, quantity: i.quantity })),
        notes: comment.trim() || undefined,
      });
      if (data?.order) {
        setOrderData({
          orderNumber:          data.order.orderNumber ?? data.order.order_number,
          totalAmountFormatted: formatRestaurantPrice(
            data.order.totalAmount ?? data.order.total_amount ?? 0,
            currency
          ),
          paymeLink:            data.paymentLinks?.payme,
          clickLink:            data.paymentLinks?.click,
          uzumLink:             data.paymentLinks?.uzum,
          paymentEnabled:       data.paymentEnabled !== false,
        });
      }
    } catch (err) {
      if (err.response?.status === 403) {
        setFrozenMsg('⚠️ Приём заказов временно приостановлен');
      } else if (err.response?.status === 429) {
        setError('Слишком много запросов. Подожди минуту и попробуй снова.');
      } else {
        setError(err.response?.data?.error || 'Не удалось создать заказ');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCall = async (type) => {
    if (callSent) return;
    setFrozenMsg(null);
    try {
      await axios.post('/api/orders/call-waiter', {
        restaurantId: restaurantId || 'a0eebc99-9c0b-4ef8-bb6d-6bb9bd380a11',
        tableNumber,
        type,
      });
      setCallSent(type);
      setTimeout(() => setCallSent(null), 4000);
    } catch (err) {
      if (err.response?.status === 403) {
        setFrozenMsg('⚠️ Приём заказов временно приостановлен');
      } else if (err.response?.status === 429) {
        setFrozenMsg('⚠️ Слишком много запросов с этой сети. Попробуй через минуту.');
      } else {
        console.error('call error:', err);
      }
    }
  };

  const handleCloseAndClear = () => {
    setOrderData(null);
    onClear?.();
    onClose?.();
  };

  // Динамические inline-стили на основе темы
  const chipBg      = isDark ? '#2A2A2A' : colors.bgChip;
  const borderColor = isDark ? '#2A2A2A' : colors.border;
  const textMain    = colors.text;
  const textSub     = colors.textSecondary;
  const inputBg     = isDark ? '#1E1E1E' : '#FFFFFF';
  const sheetBg     = colors.bgCard;

  if (items.length === 0 && !orderData) return null;

  const wrapPortal = (node) => createPortal(node, document.body);

  if (orderData) {
    return wrapPortal(
      <div
        className={`cart-sheet cart-sheet--portal${isDark ? ' cart-sheet-dark' : ''}`}
        style={{ backgroundColor: sheetBg }}
      >
        <div className="cart-handle" />
        <div style={{ textAlign: 'center', padding: '24px 0 16px' }}>
          <div style={{ fontSize: '52px', marginBottom: '12px' }}>✅</div>
          <div style={{ fontSize: '20px', fontWeight: '700', color: textMain, marginBottom: '6px' }}>
            {t.orderNum}{orderData.orderNumber} {t.orderCreated}
          </div>
          <div style={{ fontSize: '14px', color: textSub }}>{t.table} {tableNumber}</div>
        </div>

        <div style={{
          backgroundColor: chipBg, borderRadius: '14px',
          padding: '16px', margin: '16px 0', textAlign: 'center',
        }}>
          <div style={{ fontSize: '13px', color: textSub, marginBottom: '4px' }}>{t.toPay}</div>
          <div style={{ fontSize: '22px', fontWeight: '700', color: textMain }}>
            {orderData.totalAmountFormatted}
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {orderData.paymentEnabled && orderData.paymeLink && (
            <a href={orderData.paymeLink} target="_blank" rel="noopener noreferrer"
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '16px', borderRadius: '14px',
                backgroundColor: '#00aaff', color: '#fff',
                fontSize: '16px', fontWeight: '600', textDecoration: 'none',
              }}>
              💳 {t.payPayme}
            </a>
          )}
          {orderData.paymentEnabled && orderData.clickLink && (
            <a href={orderData.clickLink} target="_blank" rel="noopener noreferrer"
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '16px', borderRadius: '14px',
                backgroundColor: '#FF6B35', color: '#fff',
                fontSize: '16px', fontWeight: '600', textDecoration: 'none',
              }}>
              ⚡ Оплатить через Click
            </a>
          )}
          {orderData.paymentEnabled && orderData.uzumLink && (
            <a href={orderData.uzumLink} target="_blank" rel="noopener noreferrer"
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '16px', borderRadius: '14px',
                backgroundColor: '#7B2FBE', color: '#fff',
                fontSize: '16px', fontWeight: '600', textDecoration: 'none',
              }}>
              💜 Оплатить через Uzum
            </a>
          )}
          <button onClick={handleCloseAndClear} style={{
            width: '100%', padding: '16px', borderRadius: '14px',
            border: 'none', backgroundColor: chipBg,
            color: textMain, fontSize: '16px', fontWeight: '600', cursor: 'pointer',
          }}>{t.close}</button>
        </div>
      </div>,
    );
  }

  return wrapPortal(
    <div
      className={`cart-sheet cart-sheet--portal${isDark ? ' cart-sheet-dark' : ''}`}
      style={{ backgroundColor: sheetBg }}
    >
      <div className="cart-handle" />

      <div style={{
        display: 'flex', justifyContent: 'space-between',
        alignItems: 'center', marginBottom: '4px',
      }}>
        <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: textMain }}>
          Ваш заказ
        </h2>
        <button onClick={onClose} style={{
          width: '32px', height: '32px', borderRadius: '50%',
          backgroundColor: chipBg, border: 'none',
          color: textSub, fontSize: '20px', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>×</button>
      </div>

      <div style={{ margin: '8px 0' }}>
        {items.map((item) => (
          <div key={item.id} style={{
            display: 'flex', alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 0',
            borderBottom: `1px solid ${borderColor}`,
            gap: '12px',
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontSize: '15px', fontWeight: '500', color: textMain,
                marginBottom: '8px', lineHeight: '1.3',
              }}>
                {localizeItem(item, lang)}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <button onClick={() => onRemove(item.id)} style={{
                  width: '30px', height: '30px', borderRadius: '8px',
                  backgroundColor: chipBg, border: 'none',
                  color: textMain, fontSize: '18px', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>−</button>
                <span style={{
                  fontSize: '15px', fontWeight: '600', color: textMain,
                  minWidth: '20px', textAlign: 'center',
                }}>{item.quantity}</span>
                <button onClick={() => onAdd(item)} style={{
                  width: '30px', height: '30px', borderRadius: '8px',
                  backgroundColor: '#3081f6', border: 'none',
                  color: '#fff', fontSize: '18px', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>+</button>
              </div>
            </div>
            <div style={{
              fontSize: '15px', fontWeight: '600', color: textMain,
              textAlign: 'right', flexShrink: 0, whiteSpace: 'nowrap',
            }}>
              {formatRestaurantPrice(item.price * item.quantity, currency)}
            </div>
          </div>
        ))}
      </div>

      <div style={{ marginBottom: '12px' }}>
        <div style={{ fontSize: '13px', color: textSub, marginBottom: '6px' }}>{t.comment}</div>
        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder={t.commentHint}
          rows={2}
          style={{
            width: '100%', padding: '12px', borderRadius: '12px',
            border: `1px solid ${borderColor}`, backgroundColor: inputBg,
            color: textMain, fontSize: '14px', resize: 'vertical',
            boxSizing: 'border-box', outline: 'none',
          }}
        />
      </div>

      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '14px 0 16px',
        fontSize: '18px', fontWeight: '700', color: textMain,
        borderTop: `1px solid ${borderColor}`,
      }}>
        <span>{t.total}:</span>
        <span>{formatRestaurantPrice(totalAmount, currency)}</span>
      </div>

      {frozenMsg && (
        <div style={{
          color: '#FFA500', fontSize: '13px', textAlign: 'center',
          marginBottom: '10px', padding: '10px',
          backgroundColor: isDark ? '#2A1E00' : '#FFF8E1',
          borderRadius: '10px',
          border: '1px solid #FFA500',
        }}>
          {frozenMsg}
        </div>
      )}

      {error && (
        <div style={{
          color: '#FF6B6B', fontSize: '13px', textAlign: 'center',
          marginBottom: '10px', padding: '10px',
          backgroundColor: isDark ? '#2A1A1A' : '#FFF0F0',
          borderRadius: '10px',
        }}>⚠️ {error}</div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>

        <button
          onClick={handleCreateOrder}
          disabled={isLoading}
          style={{
            width: '100%', padding: '16px', borderRadius: '14px', border: 'none',
            backgroundColor: isLoading ? (isDark ? '#555' : '#ccc') : '#FF5722',
            color: '#fff', fontSize: '16px', fontWeight: '700',
            cursor: isLoading ? 'not-allowed' : 'pointer',
            letterSpacing: '0.2px',
          }}
        >
          {isLoading ? '⏳ ...' : `🧾 ${t.order}`}
        </button>

        <button
          onClick={() => handleCall('waiter')}
          style={{
            width: '100%', padding: '15px', borderRadius: '14px',
            backgroundColor: callSent === 'waiter'
              ? '#1A3A2A'
              : (isDark ? '#1E1E2E' : colors.bgChip),
            color: callSent === 'waiter' ? '#34C759' : textMain,
            fontSize: '15px', fontWeight: '600',
            cursor: callSent ? 'default' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
            transition: 'all 0.3s ease',
            border: callSent === 'waiter'
              ? '1px solid #34C759'
              : `1px solid ${borderColor}`,
          }}
        >
          <span style={{ fontSize: '18px' }}>🛎️</span>
          <span>{callSent === 'waiter' ? t.waiterComing : t.callWaiter}</span>
        </button>

        <button
          onClick={() => handleCall('bill')}
          style={{
            width: '100%', padding: '15px', borderRadius: '14px',
            backgroundColor: callSent === 'bill'
              ? '#1A2A3A'
              : (isDark ? '#1E1E2E' : colors.bgChip),
            color: callSent === 'bill' ? '#00aaff' : textMain,
            fontSize: '15px', fontWeight: '600',
            cursor: callSent ? 'default' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
            transition: 'all 0.3s ease',
            border: callSent === 'bill'
              ? '1px solid #00aaff'
              : `1px solid ${borderColor}`,
          }}
        >
          <span style={{ fontSize: '18px' }}>💳</span>
          <span>{callSent === 'bill' ? t.billComing : t.askBill}</span>
        </button>

      </div>
    </div>,
  );
}
